import React, { useMemo, useState } from "react"
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  Tabs,
  Checkbox,
  InputNumber,
  TimePicker,
  message,
} from "antd"
import { getFunctions, httpsCallable } from "firebase/functions"
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore"
import { getAuth, sendPasswordResetEmail } from "firebase/auth"
import { db } from "../firebase/config"

// ✨ Admin User Manager (JavaScript)
// - Create a user (via callable admin function) and auto-create Firestore users/{uid}
// - Optionally send password reset email
// - Assign roles (custom claims) in bulk
// - Edit groups for an existing user

const { Title, Text } = Typography

export const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "semiAdmin", label: "Semi Admin" },
  { value: "paperManager", label: "Paper Manager" },
  { value: "designer", label: "Designer" },
  { value: "accountant", label: "Accountant" },
  { value: "arsoma", label: "Arsoma" },
  { value: "viva", label: "Viva" },
  { value: "warehouse", label: "Warehouse" },
  { value: "winder", label: "Winder" },
  { value: "sales", label: "Sales" },
  { value: "tape", label: "Tape" },
  { value: "cutter", label: "Cutter" },
  { value: "devSpecialist", label: "Dev Specialist" },
  { value: "courier", label: "Courier" },
]

// Suggested groups (you can type new ones because the control uses mode="tags")
const GROUP_SUGGESTIONS = ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))

const roleTagColor = {
  admin: "magenta",
  semiAdmin: "geekblue",
  paperManager: "volcano",
  designer: "purple",
  accountant: "gold",
  arsoma: "cyan",
  viva: "blue",
  warehouse: "orange",
  winder: "green",
  sales: "lime",
  tape: "default",
  cutter: "red",
  devSpecialist: "geekblue",
  courier: "blue",
}

function RoleTags({ roles }) {
  if (!roles || roles.length === 0) return <Text type="secondary">No roles selected</Text>
  return (
    <Space size={[8, 8]} wrap>
      {roles.map((r) => (
        <Tag key={r} color={roleTagColor[r] || "default"}>
          {ROLE_OPTIONS.find((o) => o.value === r)?.label || r}
        </Tag>
      ))}
    </Space>
  )
}

export default function AdminUserManager() {
  const [rolesForm] = Form.useForm()
  const [createForm] = Form.useForm()
  const [groupsForm] = Form.useForm()

  const [bulkList, setBulkList] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [creating, setCreating] = useState(false)
  const [createdUser, setCreatedUser] = useState(null)
  const [updatingGroups, setUpdatingGroups] = useState(false)

  const functions = getFunctions()
  const auth = getAuth()

  // Prefer single callable if present
  const setUserRoles = useMemo(() => httpsCallable(functions, "setUserRoles"), [functions])

  // Fallback per-role callables (the ones you already deployed)
  const perRoleCallable = useMemo(
    () => ({
      admin: httpsCallable(functions, "addAdminRole"),
      semiAdmin: httpsCallable(functions, "addSemiAdmin"),
      paperManager: httpsCallable(functions, "addPaperManager"),
      designer: httpsCallable(functions, "addDesignerRole"),
      accountant: httpsCallable(functions, "addAccountant"),
      arsoma: httpsCallable(functions, "addArsoma"),
      viva: httpsCallable(functions, "addViva"),
      warehouse: httpsCallable(functions, "addWarehouse"),
      winder: httpsCallable(functions, "addWinder"),
      sales: httpsCallable(functions, "addSales"),
      tape: httpsCallable(functions, "addTape"),
      cutter: httpsCallable(functions, "addCutter"),
      devSpecialist: httpsCallable(functions, "addDevSpecialist"),
    }),
    [functions],
  )

  const rolesWatch = Form.useWatch?.("roles", rolesForm) || []

  const parseBulkEmails = () =>
    bulkList
      .split(/[\n,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e && /.+@.+\..+/.test(e))

  async function assignRolesToEmail(email, roles) {
    try {
      try {
        await setUserRoles({ email, roles })
        return { ok: true }
      } catch (err) {
        const code = (err && (err.code || err.message)) || ""
        const looksMissing =
          typeof code === "string" && /not[-_\s]?found|unimplemented|unavailable/i.test(code)
        if (!looksMissing) throw err
      }
      for (const role of roles) {
        const fn = perRoleCallable[role]
        if (fn) {
          // eslint-disable-next-line no-await-in-loop
          await fn({ email })
        }
      }
      return { ok: true }
    } catch (error) {
      return { ok: false, error: error?.message || String(error) }
    }
  }

  // ---- CREATE USER TAB ----
  const handleCreateUser = async (values) => {
    const {
      email,
      password, // optional; if empty, backend should generate
      name,
      notifEmail,
      extraPercentage = 0,
      notifications = false,
      hasAppLogin = true,
      expoPushToken,
      sendDailyOrderRecapMobileNotification = false,
      sendOrderCompletedMobileNotification = false,
      sendOrderDoneByDesignerMobileNotification = false,
      notificationTime, // dayjs or null
      roles = [],
      group = [],
      sendReset = false,
    } = values

    setCreating(true)
    setCreatedUser(null)

    try {
      const adminCreateUser = httpsCallable(functions, "adminCreateUser")
      const payload = {
        email,
        password: password || null,
        profile: {
          email,
          name: name || null,
          notifEmail: notifEmail || null,
          extraPercentage: Number(extraPercentage) || 0,
          notifications: !!notifications,
          hasAppLogin: !!hasAppLogin,
          expoPushToken: expoPushToken || null,
          group: Array.isArray(group) ? group : [],
          mobileAppLastLogin: null,
          notificationTime: notificationTime ? notificationTime : null,
          sendDailyOrderRecapMobileNotification: !!sendDailyOrderRecapMobileNotification,
          sendOrderCompletedMobileNotification: !!sendOrderCompletedMobileNotification,
          sendOrderDoneByDesignerMobileNotification: !!sendOrderDoneByDesignerMobileNotification,
        },
      }

      const res = await adminCreateUser(payload)
      const { uid } = res.data || {}

      // Optional: assign initial roles as custom claims
      if (roles && roles.length) {
        const r = await assignRolesToEmail(email, roles)
        if (!r.ok) message.warning(`User created but role assignment failed: ${r.error}`)
      }

      // Optional: client-triggered password reset email
      if (sendReset) {
        try {
          await sendPasswordResetEmail(auth, email)
        } catch (e) {
          message.warning("User created but failed to send reset email.")
        }
      }

      setCreatedUser({ uid, email })
      message.success("User created")

      createForm.resetFields()
    } catch (e) {
      console.error(e)
      Modal.error({ title: "Create user failed", content: e?.message || String(e) })
    } finally {
      setCreating(false)
    }
  }

  // ---- EDIT GROUPS TAB ----
  const handleLoadGroupsByEmail = async () => {
    const email = groupsForm.getFieldValue("email")
    if (!email) return
    setUpdatingGroups(true)
    try {
      const usersRef = collection(db, "users")
      const snap = await getDocs(query(usersRef, where("email", "==", email)))
      if (snap.empty) {
        Modal.info({ title: "No user doc found for this email." })
        return
      }
      const docSnap = snap.docs[0]
      const data = docSnap.data() || {}
      groupsForm.setFieldsValue({
        group: Array.isArray(data.group) ? data.group : [],
        uid: docSnap.id,
      })
    } catch (e) {
      console.error(e)
      Modal.error({ title: "Failed to load groups", content: e?.message || String(e) })
    } finally {
      setUpdatingGroups(false)
    }
  }

  const handleSaveGroups = async (values) => {
    const { email, group } = values
    setUpdatingGroups(true)
    try {
      const usersRef = collection(db, "users")
      const snap = await getDocs(query(usersRef, where("email", "==", email)))
      if (snap.empty) {
        Modal.info({ title: "No user doc found for this email." })
        return
      }
      const docRef = doc(db, "users", snap.docs[0].id)
      await updateDoc(docRef, { group: Array.isArray(group) ? group : [] })
      message.success("Groups updated")
    } catch (e) {
      console.error(e)
      Modal.error({ title: "Failed to save groups", content: e?.message || String(e) })
    } finally {
      setUpdatingGroups(false)
    }
  }

  const rolesTab = (
    <>
      <Form
        layout="vertical"
        form={rolesForm}
        initialValues={{ roles: [] }}
        onFinish={(vals) => {
          const { email, roles } = vals
          if (!roles || roles.length === 0) {
            Modal.warning({ title: "Select at least one role" })
            return
          }
          const bulk = parseBulkEmails()
          const emails = bulk.length ? bulk : [email]
          Modal.confirm({
            title: bulk.length
              ? `Assign ${roles.length} role(s) to ${bulk.length} user(s)?`
              : `Assign ${roles.length} role(s) to ${email}?`,
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary">Roles: </Text>
                  <RoleTags roles={roles} />
                </div>
                {bulk.length ? (
                  <Alert
                    type="info"
                    message={`Bulk mode: ${bulk.length} emails parsed.`}
                    showIcon
                  />
                ) : null}
              </div>
            ),
            okText: "Assign",
            onOk: async () => {
              setSubmitting(true)
              setResult(null)
              const details = []
              for (const em of emails) {
                // eslint-disable-next-line no-await-in-loop
                const r = await assignRolesToEmail(em, roles)
                details.push({ email: em, ok: r.ok, error: r.error })
              }
              const allOk = details.every((d) => d.ok)
              setResult({
                ok: allOk,
                message: allOk ? "Roles assigned" : "Some assignments failed",
                details,
              })
              setSubmitting(false)
            },
          })
        }}
        disabled={submitting}>
        <Form.Item label="Email" name="email" rules={[{ required: true, type: "email" }]}>
          <Input placeholder="user@company.com" allowClear />
        </Form.Item>

        <Form.Item label="(Optional) Bulk emails">
          <Input.TextArea
            value={bulkList}
            onChange={(e) => setBulkList(e.target.value)}
            placeholder={"Paste emails separated by newline, comma, or semicolon"}
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
          <div style={{ marginTop: 6 }}>
            <Text type="secondary">Parsed: {parseBulkEmails().length}</Text>
          </div>
        </Form.Item>

        <Form.Item
          label="Roles"
          name="roles"
          rules={[{ required: true, message: "Select at least one" }]}>
          <Select
            mode="multiple"
            allowClear
            options={ROLE_OPTIONS}
            placeholder="Select role(s)"
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item label="Preview">
          <RoleTags roles={rolesWatch || []} />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Assign roles
          </Button>
          <Button
            onClick={() => {
              rolesForm.resetFields()
              setBulkList("")
              setResult(null)
            }}
            disabled={submitting}>
            Reset
          </Button>
        </Space>
      </Form>

      {result && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          {result.ok ? (
            <Alert type="success" message={result.message} showIcon />
          ) : (
            <Alert type="warning" message={result.message} showIcon />
          )}

          {result.details && (
            <div style={{ marginTop: 12 }}>
              {result.details.map((d) => (
                <Card key={d.email} size="small" style={{ marginBottom: 8 }}>
                  <Space>
                    <Text strong>{d.email}</Text>
                    {d.ok ? <Tag color="green">OK</Tag> : <Tag color="red">Failed</Tag>}
                    {!d.ok && d.error ? <Text type="secondary">{d.error}</Text> : null}
                  </Space>
                </Card>
              ))}
            </div>
          )}

          <Text type="secondary">
            Users may need to sign out/in to refresh ID token and see updated claims.
          </Text>
        </>
      )}
    </>
  )

  const createTab = (
    <Form
      layout="vertical"
      form={createForm}
      onFinish={handleCreateUser}
      initialValues={{
        extraPercentage: 0,
        notifications: false,
        hasAppLogin: true,
        sendDailyOrderRecapMobileNotification: false,
        sendOrderCompletedMobileNotification: false,
        sendOrderDoneByDesignerMobileNotification: false,
        roles: [],
        group: [],
      }}>
      <Title level={5}>Account</Title>
      <Form.Item label="Email" name="email" rules={[{ required: true, type: "email" }]}>
        <Input placeholder="user@company.com" />
      </Form.Item>
      <Form.Item label="Password (optional — autogenerated if empty)" name="password">
        <Input.Password placeholder="Leave blank to auto-generate" />
      </Form.Item>
      <Form.Item name="sendReset" valuePropName="checked">
        <Checkbox>Send password reset email after creation</Checkbox>
      </Form.Item>

      <Divider />
      <Title level={5}>Profile</Title>
      <Form.Item label="Name" name="name">
        <Input placeholder="Full name" />
      </Form.Item>
      <Form.Item
        label="Notification email"
        name="notifEmail"
        rules={[{ type: "email", warningOnly: true }]}>
        <Input placeholder="notifications go here (optional)" />
      </Form.Item>
      <Form.Item label="Extra percentage" name="extraPercentage">
        <InputNumber style={{ width: "100%" }} min={0} max={100} />
      </Form.Item>
      <Form.Item name="notifications" valuePropName="checked">
        <Checkbox>Enable notifications</Checkbox>
      </Form.Item>
      <Form.Item name="hasAppLogin" valuePropName="checked">
        <Checkbox>User can log in to app</Checkbox>
      </Form.Item>
      <Form.Item label="Expo push token" name="expoPushToken">
        <Input placeholder="ExponentPushToken[...] (optional)" />
      </Form.Item>
      <Form.Item label="Notification time" name="notificationTime">
        <TimePicker format="HH:mm" allowClear />
      </Form.Item>
      <Form.Item label="Groups" name="group">
        <Select
          mode="tags"
          options={GROUP_SUGGESTIONS}
          placeholder="Add/select groups"
          tokenSeparators={[","]}
        />
      </Form.Item>

      <Divider />
      <Title level={5}>Initial roles (custom claims)</Title>
      <Form.Item name="roles">
        <Select
          mode="multiple"
          allowClear
          options={ROLE_OPTIONS}
          placeholder="Select role(s)"
          optionFilterProp="label"
        />
      </Form.Item>

      <Space>
        <Button type="primary" htmlType="submit" loading={creating}>
          Create user
        </Button>
        <Button
          onClick={() => {
            createForm.resetFields()
            setCreatedUser(null)
          }}
          disabled={creating}>
          Reset
        </Button>
      </Space>

      {createdUser && (
        <>
          <Divider />
          <Alert
            type="success"
            showIcon
            message={`Created ${createdUser.email}`}
            description={<Text copyable>{createdUser.uid}</Text>}
          />
        </>
      )}
    </Form>
  )

  const groupsTab = (
    <Form layout="vertical" form={groupsForm} onFinish={handleSaveGroups}>
      <Form.Item label="User email" name="email" rules={[{ required: true, type: "email" }]}>
        <Input placeholder="user@company.com" />
      </Form.Item>
      <Space>
        <Button onClick={handleLoadGroupsByEmail} loading={updatingGroups}>
          Load groups
        </Button>
      </Space>
      <Form.Item label="Groups" name="group">
        <Select
          mode="tags"
          options={GROUP_SUGGESTIONS}
          placeholder="Edit groups"
          tokenSeparators={[","]}
        />
      </Form.Item>
      <Space>
        <Button type="primary" htmlType="submit" loading={updatingGroups}>
          Save groups
        </Button>
      </Space>
    </Form>
  )

  return (
    <Card bordered style={{ maxWidth: 820 }}>
      <Title level={4} style={{ marginBottom: 8 }}>
        Admin — Users
      </Title>
      <Text type="secondary">Create users, manage custom-claim roles, and edit user groups.</Text>
      <Divider />
      <Tabs
        items={[
          { key: "create", label: "Create User", children: createTab },
          { key: "roles", label: "Assign Roles", children: rolesTab },
          { key: "groups", label: "Edit Groups", children: groupsTab },
        ]}
      />
    </Card>
  )
}
